import * as React from "react";
import clsx from "clsx";
import { useForm, SubmitHandler } from "react-hook-form";
import * as Checkbox from "@radix-ui/react-checkbox";
import * as Label from "@radix-ui/react-label";
import Heading from "@components/heading";
import { CheckIcon } from "icons/components";

export type PropositionContactFormInputs = {
	firstName: string;
	lastName: string;
	email: string;
	message: string;
	subject: string;
	phoneNumber?: string;
	company?: string;
};

const PropositionContactForm = ({ title, subject }) => {
	const [formSubmittedSuccessfully, setFormSubmittedSuccessfully] =
		React.useState(false);
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<PropositionContactFormInputs>({
		defaultValues: {
			firstName: "",
			lastName: "",
			email: "",
			message: "",
			subject,
			phoneNumber: "",
			company: "",
		},
	});

	const onSubmit: SubmitHandler<PropositionContactFormInputs> = async (
		data,
	) => {
		const res = await fetch("/api/submit-form", {
			body: JSON.stringify({
				...data,
			}),
			headers: {
				"Content-Type": "application/json",
			},
			method: "POST",
		});

		const result = await res.json();

		if (result.success) {
			reset();
			setFormSubmittedSuccessfully(true);

			return true;
		}

		return true;
	};

	if (formSubmittedSuccessfully) {
		return (
			<div className="max-w-6xl px-8 mx-auto xl:px-0">
				<Heading className="mb-6 text-3xl font-rigBold" as="p">
					Bedankt voor uw inzending.
				</Heading>
				<p className="max-w-2xl text-sm">
					Er zal zo spoedig mogelijk contact met u worden opgenomen.
				</p>
			</div>
		);
	}

	return (
		<div className="max-w-6xl px-8 mx-auto xl:px-0">
			<Heading className="mb-6 text-2xl font-rigBold" as="p">
				{title}
			</Heading>
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="grid grid-cols-1 gap-y-6 sm:gap-x-8 sm:grid-cols-2"
			>
				<div>
					<label
						htmlFor="firstName"
						className="block text-sm font-bold text-gray-700 font-rigBold"
					>
						Voornaam *
					</label>
					<div className="relative mt-1 border border-gray-300 rounded-md">
						<input
							{...register("firstName", { required: true })}
							autoComplete="given-name"
							className="block w-full px-4 py-3 border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-primary"
						/>
					</div>
				</div>
				<div>
					<label
						htmlFor="lastName"
						className={clsx(
							"block text-gray-700 font-rigBold text-sm font-bold",
						)}
					>
						Achternaam *
					</label>
					<div className="relative mt-1 border border-gray-300 rounded-md">
						<input
							{...register("lastName", { required: true })}
							autoComplete="family-name"
							className="block w-full px-4 py-3 border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-primary"
						/>
					</div>
				</div>
				<div className="sm:col-span-2">
					<label
						htmlFor="company"
						className="block text-sm font-bold text-gray-700 font-rigBold"
					>
						Bedrijfsnaam (optioneel)
					</label>
					<div className="relative mt-1 border border-gray-300 rounded-md">
						<input
							{...register("company")}
							autoComplete="organization"
							className="block w-full px-4 py-3 border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-primary"
						/>
					</div>
				</div>
				<div className="sm:col-span-2">
					<label
						htmlFor="email"
						className="block text-sm font-bold text-gray-700 font-rigBold"
					>
						E-mail *
					</label>
					<div className="relative mt-1 border border-gray-300 rounded-md">
						<input
							{...register("email", { required: true })}
							autoComplete="email"
							className="block w-full px-4 py-3 border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-primary"
						/>
					</div>
				</div>
				<div className="sm:col-span-2">
					<label
						htmlFor="phoneNumber"
						className="block text-sm font-bold text-gray-700 font-rigBold"
					>
						Telefoonnummer (optioneel)
					</label>
					<div className="relative mt-1 border border-gray-300 rounded-md">
						<input
							{...register("phoneNumber")}
							autoComplete="tel"
							className="block w-full px-4 py-3 border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-primary"
							placeholder="+31 612345678"
						/>
					</div>
				</div>
				<input {...register("subject")} value={subject} className="hidden" />
				<div className="sm:col-span-2">
					<label
						htmlFor="message"
						className="block text-sm font-bold text-gray-700 font-rigBold"
					>
						Bericht *
					</label>
					<div className="mt-1">
						<textarea
							{...register("message", { required: true })}
							rows={4}
							className="block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring focus:ring-primary"
							defaultValue={""}
						/>
					</div>
				</div>
				<div className="sm:col-span-2">
					<div className="flex items-start">
						<div className="flex-shrink-0">
							<Checkbox.Root
								id="acceptTerms"
								className="w-4 h-4 rounded text-primary focus:outline-none focus:ring ring-1 ring-primary"
								required
							>
								<Checkbox.Indicator asChild>
									<CheckIcon />
								</Checkbox.Indicator>
							</Checkbox.Root>
						</div>
						<div className="ml-3">
							<Label.Root
								htmlFor="acceptTerms"
								className="text-base text-gray-500 font-rigMedium"
							>
								Ik ga akkoord met de{" "}
								<a
									href="/privacy-voorwaarden"
									target="_blank"
									rel="noreferrer"
									className="font-medium text-gray-700 underline"
								>
									Privacy Voorwaarden
								</a>
								.
							</Label.Root>
						</div>
					</div>
				</div>
				<div className="sm:col-span-2">
					<button
						type="submit"
						className="inline-flex items-center justify-center w-full px-6 py-3 text-base font-bold text-white border border-transparent rounded-md shadow-sm font-rigBold bg-primary hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
					>
						Versturen
					</button>
				</div>
			</form>
		</div>
	);
};

export default PropositionContactForm;
